/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    ViewChild
} from '@angular/core';
import { BaseComponent } from '../base.component';
import { ErrorResponseModel } from '../../models';
import { LogService, NotifyService, UserService } from '../../services';
import { Observable, Subscription, timer } from 'rxjs';
import { TwoFactorAuthApiService } from '../../http';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-two-factor-auth',
    templateUrl: './two-f-a.component.html',
    styleUrls: ['./two-f-a.component.scss'],
})

export class TwoFAComponent extends BaseComponent implements AfterViewInit {
    /**
     * Reference to the hidden form input.
     */
    @ViewChild('twoFactorAuth') authInput: ElementRef;

    /**
     * Type of authentication.
     */
    @Input() public authType: 'SMS' | 'APP' = 'SMS';

    /**
     * Incoming user details, used at login.
     * @param userDetails
     */
    @Input() set userDetails(userDetails: any) {
        if (userDetails) {
            this.authType = userDetails?.hashedNumber ? 'SMS' : 'APP';
            this._userDetails = userDetails;
        } else {
            this.logService.error('TwoFAComponent:set userDetails', 'No user details found');
        }
    }

    /**
     * External call back for resend code;
     */
    @Input() public callBack: (...args: any[]) => void;

    /**
     * Output once the key has been downloaded.
     */
    @Output() public authenticated: EventEmitter<any> = new EventEmitter();

    /**
     * Account form group.
     */
    public twoFactorAuthForm: FormGroup;

    /**
     * Store the authCode value for styling in the view.
     */
    public authInputValue: string = '';

    /**
     * Display the time remaining until reset.
     */
    public timeRemaining: number = 61;

    /**
     * Fake disable to trigger class but not disable the form.
     */
    public fakeDisabled: boolean = false;

    /**
     * Private variable for incoming userId.
     * @private
     */
    private _userDetails: any = null;

    /**
     * Set up a subscriber for the countdown to appear in the view.
     * @private
     */
    private countDownSubscriber: Subscription;

    constructor(
        public formBuilder: FormBuilder,
        private cdr: ChangeDetectorRef,
        private logService: LogService,
        private notifyService: NotifyService,
        private twoFactorAuthApiService: TwoFactorAuthApiService,
        private userService: UserService
    ) {
        super();

        this.setupFormGroup();
        this.setUpFormSubscribers();
    }

    ngAfterViewInit(): void {
        if (this.authType === 'SMS') {
            this.countDown();
        }

        this.authInput.nativeElement.focus();
        this.cdr.detectChanges();
    }

    get userDetails() {
        return this._userDetails;
    }

    /**
     * Just in case the user clicks on the visible inputs, move the focus back to the hidden input.
     */
    public moveFocusToInput(): void {
        this.authInput.nativeElement.focus();
    }

    /**
     * Check if the active element is the authCode element
     */
    public inputIsActive(): boolean {
        return document.activeElement === this.authInput?.nativeElement;
    }

    /**
     * Get the part of the auth for the visible inputs by index.
     * @param index
     */
    public getAuthPart(index: number): string {
        const authCode = this.authInputValue;
        if (authCode?.length > index) {
            return authCode.charAt(index);
        }
    }

    /**
     * Resend auth code.
     */
    public resendCode(): void {
        this.authInputValue = '';
        this.timeRemaining = 31;
        const authInput = this.twoFactorAuthForm.get('authCode');
        authInput.enable();
        authInput.setValue(null);
        authInput.setErrors({ 'incorrectAutoCode': null });
        this.countDown();

        if (this.callBack) {
            this.callBack();
        } else {
            this.logService.error('TwoFAComponent:resendCode', 'No callBack function found.');
        }
    }

    /**
     * Display a counter down timer for the period of time the auth code is valid.
     * @private
     */
    private countDown(): void {
        this.countDownSubscriber = timer(0, 1000)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    if (this.timeRemaining <= 1) {
                        this.countDownSubscriber.unsubscribe();
                        const authInput = this.twoFactorAuthForm.get('authCode');
                        authInput.markAsUntouched();
                        authInput.disable({ emitEvent: false });
                        authInput.setValue(null, { emitEvent: false });
                        authInput.setErrors({ 'incorrectAutoCode': null }, { emitEvent: false });
                        authInput.updateValueAndValidity({ emitEvent: false });
                    }
                    --this.timeRemaining;
                }
            });
    }

    /**
     * Setup auth form.
     * @private
     */
    private setupFormGroup(): void {
        this.twoFactorAuthForm = this.formBuilder.group({
            authCode: [null]
        });
    }

    /**
     * Subscribe to form changes.
     * @private
     */
    private setUpFormSubscribers(): void {
        const authInput = this.twoFactorAuthForm.get('authCode');
        authInput.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (authCode: any): void => {
                    if (authInput.hasError('incorrectAutoCode')) {
                        authInput.setErrors({ 'incorrectAutoCode': null });
                    }

                    if (authCode !== null && authCode !== undefined) {
                        this.authInputValue = authCode ? authCode : '';
                        this.verifyAuthCode(authInput, authCode);
                    }
                }
            });
    }

    /**
     * Check the validity of the auth code provided.
     * @private
     */
    private verifyAuthCode(authInput: AbstractControl<any, any>, authCode: string | any[]): void {
        // Check code with the API if valid emit true
        if (authCode.length === 6) {
            this.fakeDisabled = true;

            if (this.userDetails) {
                this.notifyService.muteLocalNotifications();
            }

            const userId = (this.userDetails?.id) || this.userService.getUser('id');
            const apiCall: Observable<any> = this.userDetails
                ? this.twoFactorAuthApiService.loginVerify(this.userDetails, userId, { otpCode: authCode.toString() })
                : this.twoFactorAuthApiService.verify(userId, { otpCode: authCode.toString() });

            apiCall
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (data: any): void => {
                        this.notifyService.unmuteLocalNotifications();
                        this.authenticated.next(data);
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.fakeDisabled = false;
                        authInput.setValue(null);
                        this.authInputValue = '';
                        authInput.markAsTouched();
                        authInput.setErrors(
                            { 'incorrectAutoCode': 'accounts_two_factor_auth_incorrect_code' },
                            { emitEvent: false }
                        );

                        this.logService.error(
                            'AccountsTwoFactorAuthComponent:setupTwoFactorAuth',
                            'Error setting up Two Factor Auth',
                            error
                        );
                    }
                });
        }
    }
}

